/* eslint-disable @nx/enforce-module-boundaries */
import { CACHE_CONFIG_TYPES } from '@intigral/constant';
import { IChannelEntity } from '@intigral/domain';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { getConfiguration } from '../hooks/useConfiguration';
import to from 'await-to-js';
import moment from 'moment';
import { getChannelSchedulesApi } from '../services/serviceWrapper/ChannelsServiceWrapper';
import user from './user';
import { getUserInfo } from './../utils/user';

interface Program {
    startTime: number;
    endTime: number;
}

interface ScheduleResponse {
    listings: Program[];
    channelId: string;
}

class ChannelsUtil {
    private userChannels: IChannelEntity[];
    private channelSchedules: { [key: string]: ScheduleResponse };
    private selectedChannel: IChannelEntity;

    constructor() {
        this.userChannels = [];
        this.channelSchedules = {};
        this.selectedChannel = {} as IChannelEntity;
    }

    /**
     * Function to append start and end time to a URL.
     * @param url
     * @param startTime
     * @param endTime
     * @returns {string}
     */
    appendStartEndTime(url: string, startTime: number, endTime?: number): string {
        url +=
            '?start_time=' +
            10000 * startTime +
            '&startTimeStr=' +
            moment(startTime).format() +
            '&startTimeBP=' +
            moment(startTime).format('YYYYMMDDTHHMMSS') +
            '&startTimeEp=' +
            startTime / 1000;

        if (endTime) {
            url +=
                '&end_time=' +
                10000 * endTime +
                '&endTimeStr=' +
                moment(endTime).format() +
                '&endTimeBP=' +
                moment(endTime).format('YYYYMMDDTHHMMSS') +
                '&endTimeEp=' +
                endTime / 1000;
        }

        return url;
    }

    /**
     * Function to get the current program index.
     * @param programList
     * @returns {number}
     */
    getCurrentProgramIndex(programList: Program[]): number {
        for (let cnt = 0; cnt < programList?.length; ++cnt) {
            const program = programList[cnt];
            if (this.isNow(program)) {
                return cnt;
            }
        }
        return 0;
    }

    /**
     * Function to check if a program is earlier than the current time.
     * @param program
     * @returns {boolean}
     */
    isEarlier(program: Program): boolean {
        if (program) {
            const currentTime = Date.now();
            return program.startTime <= currentTime && program.endTime < currentTime;
        }
        return false;
    }

    /**
     * Function to check if a program is currently airing.
     * @param program
     * @returns {boolean}
     */
    // isNow(program: Program): boolean {
    //     if (program) {
    //         const currentTime = Date.now();
    //         return program.startTime <= currentTime && program.endTime >= currentTime;
    //     }
    //     return false;
    // }
    isNow(program: any, selectedDate?: string | Date): boolean {
        if (!program) return false;

        const currentTime = Date.now();

        const startingTime = Number(program.startTime);
        const endingTime = Number(program.endTime);

        if (selectedDate) {
            const selected = new Date(selectedDate);
            const now = new Date();

            const isToday = selected.toDateString() === now.toDateString();

            if (!isToday) {
                return false;
            }
        }

        return startingTime <= currentTime && endingTime >= currentTime;
    }

    /**
     * Function to check if a program is later than the current time.
     * @param program
     * @returns {boolean}
     */
    isLater(program: Program): boolean {
        if (program) {
            const currentTime = Date.now();
            return program.startTime > currentTime && program.endTime > currentTime;
        }
        return false;
    }

    /**
     * Function to sort channels by channel number.
     * @param list
     * @returns {IChannelEntity[]}
     */
    getSortedChannelByChannelNumber(list: IChannelEntity[]): IChannelEntity[] {
        const listings = list.slice(0);
        listings.sort((a, b) => {
            return Number(a.channelNumber) - Number(b.channelNumber);
        });
        return listings;
    }

    /**
     * Function to filter channels by subscription GUID, allowing PPV channels to appear.
     * @param list
     * @param subGuids
     * @returns {any[]}
     */
    getFilterChannelBySubGuid(list: any[], subGuids: string[] = null, configParam?: any): any[] {
        // Use provided config or fallback to localStorage for backward compatibility
        const config = configParam || getConfiguration();
        const PaidBaseSubscription: string[] = config?.paidVsFree?.paidSubscriptionGuids || [];
        const userInfo = user.getUserInfo();
        // Guests / incomplete sessions have no subscriptionGUID array; spreading null/undefined throws.
        const userGuids = Array.isArray(userInfo?.subscriptionGUID) ? userInfo.subscriptionGUID : [];
        const userSubGuids = subGuids ? subGuids : [...userGuids, ...PaidBaseSubscription];
        return list?.filter((channel) => {
            // Check if this is a PPV channel by subscription naming patterns
            const isPotentialPPV = channel?.subscriptions?.some((sub) => {
                const subscriptionGUID = sub.subscriptionGUID?.toLowerCase() || '';
                const subscriptionTitle = sub.subscriptionTitle?.toLowerCase() || '';
                return subscriptionGUID.includes('ppv') || subscriptionTitle.includes('ppv');
            });

            // For PPV channels, don't bypass with playLiveStream rights - let PPV filtering handle them
            if (isPotentialPPV) {
                // Always show PPV channels regardless of subscription status (PPV filtering will handle access)
                if (channel?.isPPV) return true;

                // For potential PPV channels without isPPV flag, still allow them through
                // The PPV filtering in usePPVManagement will handle the proper filtering
                return true;
            }

            // For non-PPV channels, always show channels with playLiveStream rights
            if (channel?.rights?.playLiveStream) return true;

            // Show channels if user has subscription
            if (channel?.subscriptions?.some((item) => userSubGuids?.includes(item?.subscriptionGUID))) {
                return true;
            }
            return false;
        });
    }

    /**
     * Function to sort programs by start time.
     * @param list
     * @returns {Program[]}
     */
    getSortedProgramListByStartTime(list: Program[]): Program[] {
        const listings = list.slice(0);
        listings.sort((a, b) => a.startTime - b.startTime);
        return listings;
    }

    /**
     * Function to get the closest program time to the current time.
     * @param list
     * @returns {Program}
     */
    getClosestProgramTimeToCurrentTime(list: Program[]): Program {
        const currentTime = Date.now();
        return list.reduce((prev, curr) =>
            Math.abs(curr.startTime - currentTime) < Math.abs(prev.startTime - currentTime) ? curr : prev
        );
    }

    /**
     * Function to get the program index.
     * @param item
     * @param schedules
     * @returns {number}
     */
    getProgramIndex(item: Program, schedules: Program[]): number {
        if (!schedules || !schedules.length || !item) return -1;
        return schedules.indexOf(item);
    }

    /**
     * Function to get the program by index.
     * @param index
     * @param schedules
     * @returns {Program | -1}
     */
    getProgramByIndex(index: number, schedules: Program[] = []): Program | -1 {
        if (!schedules || !schedules.length) return -1;
        return schedules[index];
    }

    /**
     * Function to format past duration.
     * @param timestamp
     * @returns {string}
     */
    formatPastDuration(timestamp: number | string): string {
        if (typeof timestamp === 'string') {
            timestamp = parseInt(timestamp, 10);
            if (isNaN(timestamp)) {
                return '';
            }
        }
        return moment(timestamp).format('MMM, D \u2014 H:mm');
    }

    /**
     * Function to get formatted time.
     * @param timestamp
     * @param format
     * @returns {string}
     */
    getFormattedTime(timestamp: number | string, format: string): string {
        if (!timestamp) return '';

        const momentObj = moment(timestamp);
        if (!momentObj.isValid()) {
            return '';
        }

        return momentObj.format(format);
    }

    /**
     * Function to check if a program is outside the rewind buffer.
     * @param startTime
     * @param rewindBuffer
     * @param isLive
     * @returns {boolean}
     */
    isOutsideRewindBuffer(startTime: number, rewindBuffer: number, isLive = false): boolean {
        const now = Date.now();
        const rewindBufferTime = now - rewindBuffer * (60 * 60 * 1000);
        return startTime && rewindBufferTime > startTime && !isLive;
    }

    /**
     * Function to get the time zone offset.
     * @returns {number}
     */
    getTimeZoneOffset(): number {
        const d = new Date();
        return -d.getTimezoneOffset() / 60;
    }

    /**
     * Function to get EPG days.
     * @returns {string[]}
     */
    getEPGDays(): string[] {
        const dates = [];
        let minusHours, plusHours;
        // let channelRewindDuration;
        // const config = getConfiguration();
        try {
            // as per contentApi config
            // minusHours = config?.contentApi?.bufferMinusHours || 12;
            // plusHours = config?.contentApi?.bufferPlusHours || 12;

            // as per tier config
            const { epgPreviousDays, epgNextDays } = getUserInfo('tierConfig');
            const prevDays = typeof epgPreviousDays === 'number' && !isNaN(epgPreviousDays) ? epgPreviousDays : 0.5;
            const nextDays = typeof epgNextDays === 'number' && !isNaN(epgNextDays) ? epgNextDays : 0.5;

            minusHours = prevDays * 24;
            plusHours = nextDays * 24;

            // channelRewindDuration = this.getRewindBuffer(this.getSelectedChannel() || ({} as IChannelEntity));
            // minusHours = Math.min(minusHours, channelRewindDuration);
        } catch (e) {
            minusHours = 12;
            plusHours = 12;
        }
        const minusDate = moment();
        const plusDate = moment();
        minusDate.subtract(minusHours, 'hours');
        plusDate.add(plusHours, 'hours');
        while (minusDate <= plusDate) {
            dates.push(minusDate.format('YYYY-MM-DD'));
            minusDate.add(1, 'day').startOf('day');
        }
        return dates;
    }

    /**
     * Calculates the EPG time window limits for a given channel based on user tier and channel rights.
     *
     * @param {Object} channel - The channel object containing EPG rights info.
     * @returns {Object} Object containing `absolutePastLimit`, `pastLimit`, and optionally `futureLimit`.
     */
    // getTimeLimits(channel) {
    //     const now = new Date();
    //     now.setHours(0, 0, 0, 0);

    //     const { rewindBuffer = 24 /*, paListingBuffer = 24*/ } = channel.rights || {};
    //     const userRewindDays = getUserInfo('tierConfig')?.rewindDuration || 1;

    //     const rewindBufferDays = Math.floor(rewindBuffer / 24);
    //     // const paListingBufferDays = Math.floor(paListingBuffer / 24);

    //     const maxRewindDays = Math.min(userRewindDays, rewindBufferDays);

    //     const absolutePastLimit = new Date(now);
    //     absolutePastLimit.setDate(now.getDate() - rewindBufferDays);

    //     const pastLimit = new Date(now);
    //     pastLimit.setDate(now.getDate() - maxRewindDays);

    //     // const futureLimit = new Date(now);
    //     // futureLimit.setDate(now.getDate() + paListingBufferDays);

    //     return {
    //         absolutePastLimit: absolutePastLimit.getTime(),
    //         pastLimit: pastLimit.getTime(),
    //         // futureLimit: futureLimit.getTime(),
    //     };
    // }

    getTimeLimits(channel) {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const { rewindBuffer = 24 } = channel?.rights ?? {};

        const tierConfig = getUserInfo('tierConfig') || {};
        const {
            rewindDuration = 1,
            rewindDurationUnit = 'days', // 'days' | 'hours'
        } = tierConfig;

        let rewindBufferInUnit;
        let userRewindInUnit;

        if (rewindDurationUnit === 'hours') {
            // Buffer is already in hours
            rewindBufferInUnit = rewindBuffer;
            userRewindInUnit = rewindDuration;
        } else {
            // Convert buffer hours → days
            rewindBufferInUnit = Math.floor(rewindBuffer / 24);
            userRewindInUnit = rewindDuration;
        }

        const maxRewind = Math.min(userRewindInUnit, rewindBufferInUnit);

        let absolutePastLimit = new Date(now);
        let pastLimit = new Date(now);

        if (rewindDurationUnit === 'hours') {
            absolutePastLimit = new Date(now.getTime() - rewindBufferInUnit * 60 * 60 * 1000);
            pastLimit = new Date(now.getTime() - maxRewind * 60 * 60 * 1000);
        } else {
            absolutePastLimit.setDate(now.getDate() - rewindBufferInUnit);
            pastLimit.setDate(now.getDate() - maxRewind);
        }

        return {
            absolutePastLimit: absolutePastLimit.getTime(),
            pastLimit: pastLimit.getTime(),
        };
    }

    /**
     * Determines if a given channel should be locked for the user at a specific target date.
     *
     * @param {Object} channel - The channel object.
     * @param {string|Date} targetDate - The date/time being checked.
     * @returns {boolean} True if the channel should be locked, false otherwise.
     */

    isLockedEpgChannel(channel, targetDate) {
        if (!channel) {
            return false;
        }

        const date = new Date(targetDate);
        date.setHours(0, 0, 0, 0);
        const targetTime = date.getTime();

        const { pastLimit } = this.getTimeLimits(channel);
        return targetTime < pastLimit;
    }

    /**
     * Filters the list of channels based on whether the target date is within
     * their valid EPG time range and annotates them with a `isLockedEpg` flag if necessary.
     *
     * @param {Array} channels - Array of channel objects.
     * @param {string|Date} targetDate - The date/time to filter channels by.
     * @returns {Array} Filtered list of channels, each possibly with `isLockedEpg` set.
     */
    getEpgFilteredChannels(channels, targetDate) {
        const date = new Date(targetDate);
        date.setHours(0, 0, 0, 0);
        const targetTime = date.getTime();

        return channels?.reduce((acc, channel) => {
            const { absolutePastLimit, pastLimit /*, futureLimit*/ } = this.getTimeLimits(channel);

            if (targetTime < absolutePastLimit /*|| targetTime > futureLimit*/) {
                return acc;
            }

            const isLockedEpg = targetTime < pastLimit;
            acc.push({ ...channel, isLockedEpg });
            return acc;
        }, []);
    }

    /**
     * Function to filter channel listings by buffer ends.
     * @param listings
     * @param start
     * @param end
     * @returns {Program[]}
     */
    filterChannelListingsByBufferEnds(listings: Program[], start: number, end: number): Program[] {
        if (!listings || !start || !end) {
            return listings || [];
        }
        return listings.filter((listingItem) => listingItem.startTime >= start && listingItem.endTime <= end);
    }

    /**
     * Function to get EPG buffer ends.
     * @returns {[number, number]}
     */
    getEPGBufferEnds(configParam?: any): [number, number] {
        const minusTime = moment();
        const plusTime = moment();
        let minusHours, plusHours, channelRewindDuration;
        // Use provided config or fallback to localStorage for backward compatibility
        const config = configParam || getConfiguration();
        try {
            minusHours = config?.contentApi.bufferMinusHours || 12;
            plusHours = config?.contentApi.bufferPlusHours || 12;
            // @ts-expect-error: getRewindBuffer might not exist on the selected channel
            channelRewindDuration = this.getRewindBuffer(this.getSelectedChannel() || {});
            minusHours = Math.min(minusHours, channelRewindDuration);
        } catch (e) {
            minusHours = 12;
            plusHours = 12;
        }
        minusTime.subtract(minusHours, 'hours');
        plusTime.add(plusHours, 'hours');
        return [minusTime.valueOf(), plusTime.valueOf()];
    }

    /**
     * Function to get the current date.
     * @returns {string}
     */
    getCurrentDate(): string {
        const d = new Date();
        return moment(d.getTime()).format('YYYY-MM-DD');
    }

    /**
     * Function to get the current date and time.
     * @returns {string}
     */
    getCurrentDateTime(): string {
        const d = new Date();
        return moment(d.getTime()).format();
    }

    /**
     * Function to get the rewind buffer for a channel.
     * @param channel
     * @returns {number}
     */
    // getRewindBuffer(channel: IChannelEntity = {} as IChannelEntity): number {
    //     const { rewindBuffer = 24 } = channel.rights || {};
    //     const tierRewindDuration = parseInt(user.getUserTierConfig().rewindDuration || '0', 10) * 24 || 12;
    //     return Math.min(rewindBuffer, tierRewindDuration);
    // }
    getRewindBuffer(channel: IChannelEntity = {} as IChannelEntity): number {
        const { rewindBuffer = 24 } = channel.rights || {};

        const tierConfig = user.getUserTierConfig() || {};

        const rewindDuration = parseInt(tierConfig.rewindDuration || '0', 10);
        const rewindDurationUnit = tierConfig.rewindDurationUnit || 'days';

        let tierRewindInHours;

        if (rewindDurationUnit === 'hours') {
            tierRewindInHours = rewindDuration || 12;
        } else {
            // default = days → convert to hours
            tierRewindInHours = (rewindDuration || 0) * 24 || 12;
        }
        return Math.min(rewindBuffer, tierRewindInHours);
    }

    /**
     * Function to get subscriptions by channel.
     * @param channel
     * @returns {string[]}
     */
    getSubscriptionsByChannel(channel: IChannelEntity): string[] {
        if (!channel || !channel.subscriptions) {
            return [];
        }
        return channel.subscriptions.map((subItem) => subItem.subscriptionGUID);
    }

    /**
     * Function to set user channels.
     * @param uC
     */
    setUserChannels(uC: IChannelEntity[]): void {
        if (!uC || !uC.length) return;
        this.userChannels = uC.slice(0);
    }

    /**
     * Function to get user channels.
     * @returns {Channel[]}
     */
    getUserChannels(): IChannelEntity[] {
        return this.userChannels;
    }

    /**
     * Function to set the selected channel.
     * @param sC
     */
    setSelectedChannel(sC: IChannelEntity): void {
        if (!sC) return;
        this.selectedChannel = sC;
    }

    /**
     * Function to get the selected channel.
     * @returns {Channel}
     */
    getSelectedChannel(): IChannelEntity {
        return this.selectedChannel;
    }

    /**
     * Function to check if a channel belongs to user channels.
     * @param channel
     * @returns {boolean}
     */
    checkChannelBelongsToUserChannels(channel: IChannelEntity): boolean {
        const { channelID } = channel;
        if (!this.userChannels.length) {
            return false;
        }
        return this.userChannels.some((ch) => ch.channelID === channelID);
    }

    /**
     * Function to get the index of a channel in a list of channels.
     * @param channel
     * @param channels
     * @returns {number}
     */
    getIndexByChannel(channel: IChannelEntity, channels: IChannelEntity[]): number {
        if (!channels || !channels.length || !channel) {
            return -1;
        }
        return channels.findIndex((chItem) => chItem.channelID === channel.channelID);
    }

    /**
     * Function to get a channel by station ID.
     * @param stationId
     * @param channels
     * @returns {Channel | undefined}
     */
    getChannelByStationId(stationId = '', channels: IChannelEntity[] = []): IChannelEntity | undefined {
        if (!stationId && !channels.length && !this.userChannels.length) {
            return undefined;
        }
        if (!channels.length) {
            channels = this.userChannels;
        }
        return channels.find((item) => item.stationURL === stationId);
    }

    /**
     * Function to get a channel by PA ID.
     * @param paId
     * @returns {Channel | undefined}
     */
    getChannelByPaId(paId = ''): IChannelEntity | undefined {
        const channels = this.getUserChannels();
        if (!paId || !channels.length) return undefined;
        return channels.find((item) => item.paId === paId);
    }

    /**
     * Function to get a channel by channel ID.
     * @param channelId
     * @returns {Channel | undefined}
     */
    getChannelByChannelId(channelId = ''): IChannelEntity | undefined {
        if (!this.userChannels.length) return undefined;
        return this.userChannels.find((ch) => ch.channelID === channelId);
    }

    /**
     * Function to get the channel schedule.
     * @param chId
     * @returns {Promise<ScheduleResponse>}
     */
    async getChannelSchedule(chId: string): Promise<ScheduleResponse> {
        // const [err, response] = await to(getChannelSchedulesApi(chId));
        // const [err, response] = await to(apis.getJawwyService().channels().getChannelSchedules(chId));
        let listings;
        // const [err2, response2] = await to<ScheduleResponse>(
        //     apis.getJawwyService().channels().getChannelSchedules(chId)
        // );
        const [err2, response2] = await to<ScheduleResponse>(getChannelSchedulesApi(chId));

        if (err2 || !response2) {
            throw err2;
        }
        if (response2) {
            listings = this.getSortedProgramListByStartTime(response2.listings);
        }
        return { listings, channelId: response2.channelId };
        // if (err || !response) {
        //     throw err;
        // }
        // if (response) {
        //     listings = this.getSortedProgramListByStartTime((response as ScheduleResponse).listings);
        // }
        // return { listings, channelId: (response as ScheduleResponse).channelId };
    }

    /**
     * Function to get the "On Now" label for a selected program.
     * @param selected
     * @returns {string}
     */
    getOnNowLabel(selected: Program): string {
        if (!selected) return '';
        const onNowLabel = this.isEarlier(selected) ? 'REWIND' : this.isLater(selected) ? 'LATER' : 'ONNOW';
        return onNowLabel.toLowerCase();
    }

    /**
     * Function to get the epoch time from a UTC time string.
     * @param utcTimeString
     * @returns {number | null}
     */
    getEpochTime(utcTimeString: string): number | null {
        return utcTimeString ? new Date(utcTimeString).getTime() : null;
    }

    getChannelImagesByStationId(stationId = '', imageType = '264x264', channels: IChannelEntity[] = []): string {
        if (!stationId && !channels.length && !this.userChannels.length) {
            return '';
        }
        if (!channels.length) {
            channels = this.userChannels;
        }
        const selectedChannel = channels.find((item) => item.stationURL === stationId);
        return (
            selectedChannel?.channelImages?.find((image) => image.id === `colour_${imageType}`)?.url ||
            selectedChannel?.channelImages?.find((image) => image.id === imageType)?.url ||
            ''
        );
    }

    getSeletedChannelImage(selectedChannel: IChannelEntity, imageType = '264x264'): string {
        const channelImageId = imageType || `colour_${imageType}`;
        return selectedChannel?.channelImages?.find((image) => image.id === channelImageId)?.url || '';
    }

    compileLiveTVGenresWithCounts(channels, favoriteChannels) {
        if (!channels || !favoriteChannels) return [];
        const filteredChannels = this.getFilterChannelBySubGuid(channels);
        const genresObj = filteredChannels
            ?.map((channel) => {
                // Safety check: ensure category is an array before spreading
                const categories = Array.isArray(channel.category) ? channel.category : [];
                return [...categories];
            })
            .flat()
            .reduce((acc, value, index) => ({ ...acc, [value]: (acc[value] || 0) + 1, All: index + 1 }), {});
        const favoriteCount = favoriteChannels.length;
        genresObj['Favorite'] = favoriteCount;

        const genresArray = Object.keys(genresObj)
            .sort()
            .map((genreKey) => {
                const normalizedKey = genreKey.toLowerCase().trim().replace(/\s+/g, '_'); // replaces one or more spaces globally
                return {
                    Id: genreKey,
                    type: 'text',
                    label: `channel_filter_${normalizedKey}_label`,
                    count: genresObj[genreKey],
                    value: genreKey,
                };
            });
        return genresArray;
    }
}

export default (() => {
    let singletonObj: ChannelsUtil;
    if (!singletonObj) {
        singletonObj = new ChannelsUtil();
    }
    return singletonObj;
})();
